import { Directionality } from 'BMapsSrc/integrations/properties_common';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { App } from 'BMapsSrc/BMapsApp';
import { JobDefinition, JobResultDestinations } from '../JobDefinition';

/**
 * Scoped-property metadata for an MD engine. Applying a job's results writes,
 * per compound: the pose verdict across independent runs, the mean ligand RMSD from the
 * submitted pose, and the protein-ligand hydrogen bond with the highest
 * occupancy.
 * @param {string} scope - the jobTypeId ('stormm_md' | 'gromacs_md')
 * @param {string} label - e.g. 'STORMM MD'
 * @returns {object}
 */
function makeMdMetadata(scope, label) {
    return deepFreeze({
        compound: {
            childGroups: {
                [scope]: {
                    metadata: {
                        label: `${label} properties`,
                        tooltipHeading: `${label} simulation`,
                    },
                    properties: {
                        pose: {
                            metadata: {
                                label: `Pose (${label})`,
                                description: 'Whether the docked pose held during MD: stable (ligand RMSD < 2 Å over the last quarter of the run), shifting (2-3 Å) or unstable (> 3 Å), by majority over the copies.',
                                type: 'string',
                                defaultVisible: true,
                                propApplicability: PropApplicability.PERCOMPOUNDTARGET,
                            },
                        },
                        ligand_rmsd: {
                            metadata: {
                                label: `Ligand RMSD (${label})`,
                                description: 'Mean ligand heavy-atom RMSD from the submitted pose after fitting the protein backbone, averaged over the independent runs.',
                                unit: 'Å',
                                directionality: Directionality.MINIMIZE,
                                defaultVisible: true,
                                propApplicability: PropApplicability.PERCOMPOUNDTARGET,
                            },
                        },
                        top_hbond: {
                            metadata: {
                                label: `Top H-bond (${label})`,
                                description: 'The protein-ligand hydrogen bond with the highest mean occupancy over the independent runs.',
                                type: 'string',
                                propApplicability: PropApplicability.PERCOMPOUNDTARGET,
                            },
                        },
                    },
                },
            },
        },
    });
}

/**
 * "A:981 VAL O ← N3 (54%)": a packet hydrogen-bond row for display.
 * @param {object} h - an `hbonds` row of the md/1 packet
 * @returns {string}
 */
export function describeHbond(h) {
    if (!h) return '';
    const arrow = h.ligand_role === 'donor' ? '←' : '→';
    const pct = Math.round(100 * (h.occupancy ?? 0));
    return `${h.residue} ${h.resname} ${h.protein_atom} ${arrow} ${h.ligand_atom} (${pct}%)`;
}

/**
 * Base class for MD jobs (STORMM / GROMACS / OpenMM). The backend runs plain MD of each
 * submitted compound in the submitted receptor and returns an `md/1` packet:
 * per compound, per copy, the pose verdict, RMSDs, H-bonds and contacts (with
 * residues as BMaps keys "CHAIN:RESNUM[ICODE]"), time series, per-residue
 * RMSF and the final pose, plus an across-copy aggregate. See
 * docs/md-job-types.md.
 *
 * `integrateResults` stores the packet on the job's local state (the Results
 * tab renders it). Writing properties onto compounds happens only when asked
 * to apply (`options.apply` or `options.items`), matched by name (resSpec).
 *
 * @augments JobDefinition
 */
export class MdSimulationJob extends JobDefinition {
    static resultDestination = JobResultDestinations.MOLECULAR_DYNAMICS;

    /** Compound properties written on Apply. */
    static scoreProps = ['pose', 'ligand_rmsd', 'top_hbond'];

    /** @override */
    getRequestMeta(jobrequest, stash) {
        const params = jobrequest?.params || {};
        const meta = {
            jobName: jobrequest?.jobName,
            method: this.constructor.jobTypeName,
            target: params.target || 'conifergpu2',
            ligandCount: jobrequest?.ligand_count,
            lengthNs: params.length_ns,
            copies: params.copies,
        };
        if (stash?.ligandSpecs) meta.ligandSpecs = stash.ligandSpecs;
        if (stash?.structure?.name) meta.structure = stash.structure.name;
        return meta;
    }

    /** The scoping fields {@link getRequestMeta} put on the job list. */
    static scopeFromRequestMeta(job) {
        const meta = job?.requestmeta;
        if (!meta?.ligandSpecs) return undefined;
        return { ligandSpecs: meta.ligandSpecs, structure: { name: meta.structure } };
    }

    /**
     * The workspace compounds a job's results may be applied to: the ones it
     * was submitted for (by resSpec), on the structure it was submitted from.
     * @param {{ ligandSpecs?: string[], structure?: { name?: string } }} [scope]
     * @param {import('BMapsModel').Compound[]} [loaded] - default: all loaded compounds
     * @returns {import('BMapsModel').Compound[]}
     */
    static candidateCompounds(scope, loaded) {
        const pool = loaded || App.Workspace.getLoadedCompounds();
        const specs = new Set(scope?.ligandSpecs || []);
        const structureName = scope?.structure?.name;
        return pool.filter((c) => {
            if (specs.size > 0 && !specs.has(c.resSpec)) return false;
            if (structureName) {
                const { mapCase } = App.getDataParents(c) || {};
                if (mapCase?.displayName && mapCase.displayName !== structureName) return false;
            }
            return true;
        });
    }

    /**
     * The md/1 packet inside a retrieve-job-results response (or a bare packet).
     * @param {object} packet
     * @returns {object|undefined}
     */
    static mdPacket(packet) {
        const p = packet?.jobResults || packet;
        return p && Array.isArray(p.compounds) ? p : undefined;
    }

    /**
     * The properties Apply writes for one compound of the packet, or null for a
     * compound that failed or has no copies.
     * @param {object} compound - a `compounds` entry of the md/1 packet
     * @returns {{ pose?: string, ligand_rmsd?: number, top_hbond?: string }|null}
     */
    static compoundProps(compound) {
        if (!compound || compound.status !== 'succeeded') return null;
        const agg = compound.aggregate || {};
        const props = {};
        if (agg.pose) props.pose = agg.pose;
        const rmsd = agg.ligand_rmsd_a?.mean;
        if (rmsd !== undefined && rmsd !== null) props.ligand_rmsd = rmsd;
        const top = (agg.hbonds || [])[0];
        if (top) props.top_hbond = describeHbond(top);
        return props;
    }

    /**
     * Store the packet for the Results tab and, when applying, write the
     * per-compound properties onto the matching workspace compounds.
     * @override
     */
    async integrateResults(job, results, options) {
        let packet = results;
        let error;
        try {
            if (!packet) return { error: 'Empty results' };
            if (typeof packet === 'string') packet = JSON.parse(packet);
            const md = MdSimulationJob.mdPacket(packet);
            if (!md) return { error: 'The results are not an MD packet', results: packet };
            const stash = packet.stash || undefined;
            // Lazy: the redux access module imports the job registry, which
            // imports this class, so a top-level import would be a cycle.
            const { updateLocalJobState } = await import('BMapsRedux/jobs/access');
            if (job?.jobid) updateLocalJobState(job.jobid, { results: md, stash });

            const { apply, loaded } = options || {};
            let { items } = options || {};
            const applying = !!apply || (Array.isArray(items) && items.length > 0);
            if (!applying) return { results: packet, summary: md };
            if (!Array.isArray(items) || items.length === 0) {
                items = MdSimulationJob.candidateCompounds(
                    stash || MdSimulationJob.scopeFromRequestMeta(job), loaded,
                );
            }
            const byName = new Map(md.compounds.map((c) => [String(c.name), c]));
            const applied = [];
            const appliedNames = new Set();
            for (const cmpd of items || []) {
                const entry = byName.get(String(cmpd.resSpec));
                const props = MdSimulationJob.compoundProps(entry);
                if (!props) continue;
                for (const [prop, value] of Object.entries(props)) {
                    cmpd.setProperty(this.jobTypeId, prop, value);
                }
                applied.push(cmpd.resSpec);
                appliedNames.add(entry.name);
            }
            const missing = md.compounds
                .filter((c) => c.status === 'succeeded' && !appliedNames.has(c.name))
                .map((c) => c.name);
            if (job?.jobid) updateLocalJobState(job.jobid, { applied, missing });
            if (applied.length === 0) {
                return {
                    error: 'None of the simulated compounds are loaded in the workspace',
                    results: packet,
                    summary: md,
                    applied,
                    missing,
                };
            }
            return {
                results: packet, summary: md, applied, missing,
            };
        } catch (ex) {
            error = `Error loading ${this.jobTypeId} results`;
            console.error(error, ex);
        }
        return { error, results: packet };
    }
}

/**
 * STORMM MD: NVT Langevin (STORMM has no barostat; the box density comes from
 * an OpenMM NPT preparation), copies batched in one GPU process.
 * @augments MdSimulationJob
 */
export class StormmMdJob extends MdSimulationJob {
    static jobTypeId = 'stormm_md';
    static jobTypeName = 'STORMM MD';
    static jobName = 'STORMM MD Job';
    static jobDescription = 'runs molecular dynamics of protein-ligand complexes (NVT) and reports pose stability, interactions and flexibility.';
    static requiresLoadedProtein = true;
    static scopedPropertyMetadata = makeMdMetadata('stormm_md', 'STORMM MD');
}

/**
 * GROMACS MD: NPT (Langevin sd integrator + C-rescale barostat).
 * @augments MdSimulationJob
 */
export class GromacsMdJob extends MdSimulationJob {
    static jobTypeId = 'gromacs_md';
    static jobTypeName = 'GROMACS MD';
    static jobName = 'GROMACS MD Job';
    static jobDescription = 'runs molecular dynamics of protein-ligand complexes (NPT) and reports pose stability, interactions and flexibility.';
    static requiresLoadedProtein = true;
    static scopedPropertyMetadata = makeMdMetadata('gromacs_md', 'GROMACS MD');
}

/**
 * OpenMM MD: NPT (LangevinMiddle integrator + Monte Carlo barostat), one
 * process per copy.
 * @augments MdSimulationJob
 */
export class OpenmmMdJob extends MdSimulationJob {
    static jobTypeId = 'openmm_md';
    static jobTypeName = 'OpenMM MD';
    static jobName = 'OpenMM MD Job';
    static jobDescription = 'runs molecular dynamics of protein-ligand complexes (NPT) and reports pose stability, interactions and flexibility.';
    static requiresLoadedProtein = true;
    static scopedPropertyMetadata = makeMdMetadata('openmm_md', 'OpenMM MD');
}
